import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import type { LastOrderModel, ReorderLine, UnavailableReason } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'
import { articlesText, unavailableLinesText } from '../text'

interface Props {
  order: LastOrderModel
  /** Whether a catalog check has run: what turns one tap into an informed second one. */
  checked: boolean
  available: ReorderLine[]
  unavailable: (ReorderLine & { availability: UnavailableReason })[]
  busy: boolean
  onRepeat: () => void
}

/**
 * The home card that puts the last order back in the cart.
 *
 * The subtitle is the one place in the storefront that can honestly say "del 6
 * ago": the date and the lines behind the button are the *same* order, read
 * from `lastOrder`. When that order carries no usable date the sentence loses
 * the date and keeps the count, rather than rendering a placeholder where a
 * date was promised — the button still works, so only the claim goes.
 *
 * It renders a model and decides nothing: which lines survive, whether a check
 * has run and whether a tap commits all belong to `useRepeatLastOrder`.
 */
export const RepeatLastOrder = ({
  order,
  checked,
  available,
  unavailable,
  busy,
  onRepeat,
}: Props) => {
  const label = (() => {
    // Two different waits behind one flag, and naming them apart is the
    // difference between "we are looking" and "we are adding".
    if (busy) return checked ? 'Agregando…' : 'Revisando disponibilidad…'
    if (unavailable.length > 0 && available.length > 0) return 'Agregar lo disponible'
    return 'Repetir mi último pedido'
  })()

  return (
    <Box
      component="section"
      aria-label="Tu último pedido"
      sx={{
        bgcolor: YALO_SEMANTIC.bgSurface,
        border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
        borderRadius: `${YALO_RADIUS.lg}px`,
        p: 1.75,
        boxShadow: YALO_SHADOW.xs,
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
          <Typography variant="overline" color="text.secondary">
            Tu último pedido
          </Typography>
          <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
            {order.dateText
              ? `Del ${order.dateText} · ${articlesText(order.units)}`
              : articlesText(order.units)}
          </Typography>
        </Stack>

        {/* Said after the check but before anything is added, so the shopper
            reads what is missing and then decides — never an alert explaining a
            cart that is already short. */}
        {unavailable.length > 0 && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {available.length === 0
              ? 'Ninguno de estos productos está disponible ahora: '
              : 'No podemos repetir todo el pedido: '}
            {unavailableLinesText(unavailable)}.
          </Alert>
        )}

        <Button
          fullWidth
          onClick={onRepeat}
          // Withheld only once the check *proved* nothing is sellable. Before
          // the check, tapping is what triggers it; on a failed check nothing
          // was stored, so the button stays armed as the retry.
          disabled={busy || (checked && available.length === 0)}
          sx={{
            height: 42,
            borderRadius: `${YALO_RADIUS.pill}px`,
            bgcolor: 'primary.light',
            color: 'primary.dark',
            fontWeight: 600,
            fontSize: 13.5,
            '&:hover': { bgcolor: 'primary.light', filter: 'brightness(0.97)' },
          }}
        >
          {label}
        </Button>
      </Stack>
    </Box>
  )
}
